import { criterion, fail, fromCriteria, pass, unsupported } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readComments } from "@/office/word/review";

export const lektionKommentare: Lesson = {
  id: "kommentare",
  title: "Kommentare schreiben und beantworten",
  summary: "Rückmeldungen geben, ohne fremden Text anzufassen.",
  durationMinutes: 8,
  learningGoals: [
    "Kommentare einfügen und beantworten",
    "Kommentare gezielt auflösen statt löschen",
    "Kommentare von Textänderungen unterscheiden",
  ],
  steps: [
    {
      id: "kommentar-einfuegen",
      title: "Einen Kommentar einfügen",
      explanation: [
        "Wer ein fremdes Dokument gegenliest, steht vor einer Frage: Ändere ich den Text " +
          "direkt oder sage ich nur Bescheid? Für alles, was eine Entscheidung der " +
          "verfassenden Person braucht, ist der Kommentar das richtige Werkzeug.",
        "Ein Kommentar hängt an einer Textstelle, verändert den Text aber nicht. Er trägt " +
          "automatisch Name und Datum und lässt sich beantworten – dadurch entsteht ein " +
          "nachvollziehbarer Gesprächsfaden direkt am betreffenden Satz.",
        "Markiere die Stelle und wähle „Überprüfen“ → „Neuer Kommentar“, oder nutze " +
          "Strg + Alt + A.",
      ],
      keyPoints: [
        "Kommentare ändern den Text nicht – sie hängen nur daran.",
        "Strg + Alt + A fügt einen Kommentar zur Markierung ein.",
        "Immer die konkrete Stelle markieren, nicht den ganzen Absatz.",
      ],
      task: {
        instruction: "Markiere eine Textstelle in deinem Dokument und schreibe einen Kommentar dazu.",
        hint: "Wort oder Satz markieren, dann Überprüfen → Neuer Kommentar.",
        check: async () => {
          const kommentare = await readComments();
          if (kommentare === null) {
            return unsupported(
              "Diese Word-Version stellt dem Add-in keine Kommentare zur Verfügung, deshalb " +
                "lässt sich die Aufgabe nicht automatisch prüfen.",
              "Kontrolliere selbst, ob dein Kommentar am Rand erscheint, und markiere den Schritt dann als erledigt.",
            );
          }
          if (kommentare.length === 0) {
            return fail(
              "Es wurde noch kein Kommentar gefunden.",
              "Der Text muss markiert sein, bevor du auf „Neuer Kommentar“ klickst.",
            );
          }
          return pass(
            `${kommentare.length} ${kommentare.length === 1 ? "Kommentar" : "Kommentare"} gefunden – der erste lautet: „${kommentare[0].content.slice(0, 50)}“.`,
          );
        },
      },
    },
    {
      id: "antworten-und-aufloesen",
      title: "Antworten und auflösen",
      explanation: [
        "Ein Kommentar ist kein Zettel, sondern ein Gespräch. Über „Antworten“ hängst du " +
          "deine Rückmeldung direkt an den bestehenden Kommentar – alles bleibt an einer " +
          "Stelle und in der richtigen Reihenfolge.",
        "Ist der Punkt geklärt, gibt es zwei Wege. „Auflösen“ markiert den Kommentar als " +
          "erledigt: Er wird ausgegraut, bleibt aber lesbar. „Löschen“ entfernt ihn " +
          "vollständig samt Verlauf.",
        "In der Zusammenarbeit ist Auflösen fast immer besser. Später lässt sich dann noch " +
          "nachvollziehen, warum eine Formulierung so gewählt wurde. Erst kurz vor der " +
          "Weitergabe nach außen räumt man auf – dazu mehr in der letzten Lektion dieses " +
          "Moduls.",
      ],
      keyPoints: [
        "„Antworten“ hält den Verlauf zusammen.",
        "„Auflösen“ blendet aus, „Löschen“ entfernt endgültig.",
        "Im Bereich „Kommentare“ siehst du alle auf einen Blick.",
      ],
      task: {
        instruction:
          "Antworte auf deinen eigenen Kommentar und lege einen zweiten Kommentar an, den " +
          "du anschließend auflöst.",
        hint: "Zum Auflösen mit der rechten Maustaste auf den Kommentar klicken und „Kommentar auflösen“ wählen.",
        check: async () => {
          const kommentare = await readComments();
          if (kommentare === null) {
            return unsupported(
              "Diese Word-Version stellt dem Add-in keine Kommentare zur Verfügung.",
              "Prüfe selbst, ob eine Antwort und ein aufgelöster Kommentar vorhanden sind, und markiere den Schritt dann als erledigt.",
            );
          }

          const mitAntwort = kommentare.filter((kommentar) => kommentar.replyCount > 0);
          const aufgeloest = kommentare.filter((kommentar) => kommentar.resolved);

          return fromCriteria(
            [
              criterion(
                `Mindestens zwei Kommentare (gefunden: ${kommentare.length})`,
                kommentare.length >= 2,
              ),
              criterion(
                `Ein Kommentar hat eine Antwort (gefunden: ${mitAntwort.length})`,
                mitAntwort.length >= 1,
              ),
              criterion(
                `Ein Kommentar ist aufgelöst (gefunden: ${aufgeloest.length})`,
                aufgeloest.length >= 1,
              ),
            ],
            {
              success: "Genau so läuft eine saubere Abstimmung im Dokument.",
              failure: "Da fehlt noch etwas:",
              hint: "Achte darauf, wirklich „Antworten“ zu benutzen statt einen zweiten eigenständigen Kommentar an dieselbe Stelle zu setzen.",
            },
          );
        },
      },
    },
  ],
};
